import { useEffect, useMemo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Stack, NumberInput, TextInput, Select, SegmentedControl, Text, Group, Box } from '@mantine/core';
import { IconLanguage } from '@tabler/icons-react';
import { WidgetNodeShell } from '../../components/flow-editor/nodes/widget-node-shell';
import { LabeledHandle } from '../../components/flow-editor/nodes/labeled-handle';
import { VariableTextarea } from '../../components/flow-editor/variable-textarea';
import type { WidgetDefinition, WidgetNodeProps, ConfigPanelProps, LanguageOption } from '../types';
import {
  getLanguagePreferenceWidgetCore,
  type GetLanguagePreferenceConfig,
} from '@legacy-shared/widgets/core/getLanguagePreference';

export { type GetLanguagePreferenceConfig } from '@legacy-shared/widgets/core/getLanguagePreference';

const DIGIT_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

function autoAssignDigits(
  current: Record<string, string>,
  languages: LanguageOption[],
  defaultLanguage: string | undefined,
): Record<string, string> {
  const next: Record<string, string> = {};
  const used = new Set<string>();

  const ordered = [...languages].sort((a, b) => {
    if (a.code === defaultLanguage) return -1;
    if (b.code === defaultLanguage) return 1;
    return 0;
  });

  for (const lang of ordered) {
    if (!(lang.code in current)) continue;
    const existing = current[lang.code];
    if (existing && DIGIT_OPTIONS.includes(existing) && !used.has(existing)) {
      next[lang.code] = existing;
      used.add(existing);
    } else {
      next[lang.code] = '';
    }
  }

  for (const lang of ordered) {
    if (lang.code in next) continue;
    const free = DIGIT_OPTIONS.find((d) => !used.has(d));
    if (!free) break;
    next[lang.code] = free;
    used.add(free);
  }

  return next;
}

function digitMapsEqual(a: Record<string, string>, b: Record<string, string>): boolean {
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key, i) => key === bKeys[i] && a[key] === b[key]);
}

function GetLanguagePreferenceNode({ id, data, selected }: WidgetNodeProps<GetLanguagePreferenceConfig>) {
  const subtitle = data.config.mode === 'set' ? `Set: ${data.config.setLanguage || 'en (default)'}` : 'Ask caller';

  return (
    <WidgetNodeShell
      icon={IconLanguage}
      title={data.label || 'Language'}
      subtitle={subtitle}
      configured={data.config.mode === 'set' ? true : !!data.config.prompt}
      errors={data.errors}
      selected={selected}
      color="grape"
      retryCount={data.config.mode === 'ask' ? data.config.retryCount : undefined}
    >
      <Handle type="target" position={Position.Top} id="in" />
      <LabeledHandle type="source" position="bottom" id="out" label="Next" offset="50%" nodeId={id} />
    </WidgetNodeShell>
  );
}

function GetLanguagePreferenceConfigPanel({
  config,
  onChange,
  availableVariables,
  languages,
  defaultLanguage,
}: ConfigPanelProps<GetLanguagePreferenceConfig>) {
  const languageOptions = (languages ?? []).map((l) => ({ value: l.code, label: l.label }));
  const flowLanguages = useMemo(() => languages ?? [], [languages]);
  const digitByLanguage = useMemo(() => config.digitByLanguage ?? {}, [config.digitByLanguage]);

  useEffect(() => {
    if (config.mode !== 'ask' || flowLanguages.length === 0) return;
    const reconciled = autoAssignDigits(digitByLanguage, flowLanguages, defaultLanguage);
    if (!digitMapsEqual(reconciled, digitByLanguage)) {
      onChange({ ...config, digitByLanguage: reconciled });
    }
  }, [config, digitByLanguage, flowLanguages, defaultLanguage, onChange]);

  const usedDigitCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const d of Object.values(digitByLanguage)) {
      if (!d) continue;
      counts[d] = (counts[d] ?? 0) + 1;
    }
    return counts;
  }, [digitByLanguage]);

  const setDigitForLanguage = (code: string, digit: string | null) => {
    const next = { ...digitByLanguage, [code]: digit ?? '' };
    onChange({ ...config, digitByLanguage: next });
  };

  return (
    <Stack gap="md">
      <div>
        <Text size="sm" fw={500} mb={4}>
          Mode
        </Text>
        <SegmentedControl
          fullWidth
          value={config.mode}
          onChange={(value) => onChange({ ...config, mode: value as 'ask' | 'set' })}
          data={[
            { value: 'ask', label: 'Ask Caller' },
            { value: 'set', label: 'Set Language' },
          ]}
        />
      </div>

      {config.mode === 'ask' && (
        <>
          <VariableTextarea
            label="Prompt"
            description="The message asking the caller to select a language"
            placeholder="For English, press 1. Para español, oprima 2."
            value={config.prompt}
            onChange={(prompt) => onChange({ ...config, prompt: prompt as string })}
            availableVariables={availableVariables}
            minRows={3}
          />

          {flowLanguages.length > 0 && (
            <Box>
              <Text size="sm" fw={500} mb={4}>
                Digit per language
              </Text>
              <Text size="xs" c="dimmed" mb="xs">
                Choose which DTMF digit selects each language. Make sure these match your prompt.
              </Text>
              <Stack gap={6}>
                {flowLanguages.map((lang) => {
                  const digit = digitByLanguage[lang.code] ?? '';
                  const isDuplicate = digit !== '' && (usedDigitCounts[digit] ?? 0) > 1;
                  return (
                    <Group key={lang.code} justify="space-between" wrap="nowrap">
                      <Text size="sm">{lang.label}</Text>
                      <Select
                        size="xs"
                        w={90}
                        value={digit || null}
                        onChange={(v) => setDigitForLanguage(lang.code, v)}
                        data={DIGIT_OPTIONS.map((d) => ({ value: d, label: d }))}
                        error={isDuplicate}
                        clearable
                      />
                    </Group>
                  );
                })}
              </Stack>
              {Object.values(usedDigitCounts).some((c) => c > 1) && (
                <Text size="xs" c="red" mt={4}>
                  Each language must use a different digit.
                </Text>
              )}
            </Box>
          )}

          <NumberInput
            label="Timeout (seconds)"
            description="How long to wait for input"
            value={config.timeout}
            onChange={(value) => onChange({ ...config, timeout: typeof value === 'number' ? value : 5 })}
            min={1}
            max={30}
          />

          <NumberInput
            label="Retry Count"
            description="Number of times to re-prompt before continuing"
            value={config.retryCount}
            onChange={(value) => onChange({ ...config, retryCount: typeof value === 'number' ? value : 1 })}
            min={0}
            max={5}
            allowDecimal={false}
          />

          {config.retryCount > 0 && (
            <VariableTextarea
              label="Retry Prompt"
              description="What the caller hears on retry"
              placeholder="We didn't receive your input. Please try again..."
              value={config.retryPrompt}
              onChange={(retryPrompt) => onChange({ ...config, retryPrompt: retryPrompt as string })}
              availableVariables={availableVariables}
              minRows={3}
            />
          )}
        </>
      )}

      {config.mode === 'set' && (
        <Select
          label="Language"
          description="The language to set"
          placeholder={languageOptions.length === 0 ? 'Configure languages on the flow first' : 'Select language'}
          value={config.setLanguage || null}
          onChange={(value) => onChange({ ...config, setLanguage: value || '' })}
          data={languageOptions}
          disabled={languageOptions.length === 0}
        />
      )}

      <TextInput
        label="Save to Variable"
        description="Variable name to store the selected language code"
        value={config.outputVariable}
        onChange={(e) => onChange({ ...config, outputVariable: e.target.value })}
      />

      {(languages ?? []).length === 0 && (
        <Text size="xs" c="orange">
          No languages configured on this flow. Add languages via the toolbar to use this widget.
        </Text>
      )}
    </Stack>
  );
}

export const getLanguagePreferenceWidget: WidgetDefinition<GetLanguagePreferenceConfig> = {
  ...getLanguagePreferenceWidgetCore,
  icon: 'IconLanguage',
  variables: getLanguagePreferenceWidgetCore.variables,

  handles: {
    inputs: [{ id: 'in', type: 'default', label: 'Input', position: 'top' }],
    outputs: [{ id: 'out', type: 'default', label: 'Next', position: 'bottom' }],
  },

  connectionRules: {
    allowedInputFrom: ['*'],
    allowedOutputTo: ['*'],
  },

  ui: {
    component: GetLanguagePreferenceNode,
    configPanel: GetLanguagePreferenceConfigPanel,
    preview: (config) => (config.mode === 'set' ? `Set: ${config.setLanguage}` : 'Ask caller'),
  },

  validation: {
    ...getLanguagePreferenceWidgetCore.validation,
    validateConnections: () => ({ valid: true, errors: [] }),
  },
};
